import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppFrame, TopBar, BottomNav } from "@/components/Shell";
import { api, resolveImage } from "@/lib/api";
import { Package, AlertTriangle, XCircle, TrendingUp, ArrowRight } from "lucide-react";

export default function Inventory() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, r] = await Promise.all([
        api.get("/inventory/summary"),
        api.get("/sales", { params: { limit: 20 } }),
      ]);
      setSummary(s.data);
      setSales(r.data);
    })();
  }, []);

  if (!summary) {
    return (
      <AppFrame>
        <TopBar title="Inventory" subtitle="Pulse" />
        <div className="px-5 mt-8 animate-pulse space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-20 rounded-2xl" style={{ background: "var(--bg-alt)" }} />)}
        </div>
      </AppFrame>
    );
  }

  const rupees = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

  return (
    <AppFrame>
      <TopBar title="Inventory" subtitle="Pulse" />

      <div className="px-5 mt-4 space-y-3">
        {/* Today's sales strip */}
        <div
          className="rounded-3xl p-5 flex items-center justify-between"
          style={{ background: "var(--ink)", color: "var(--bg)" }}
          data-testid="today-sales-card"
        >
          <div>
            <div className="text-[9px] tracking-editorial uppercase opacity-70">Today</div>
            <div className="font-display-italic text-3xl leading-none mt-1">{rupees(summary.today_revenue)}</div>
            <div className="text-[11px] opacity-70 mt-1">{summary.today_sales_count} sale{summary.today_sales_count === 1 ? "" : "s"} recorded</div>
          </div>
          <TrendingUp size={22} strokeWidth={1.3} className="opacity-70" />
        </div>

        {/* 3 stat tiles */}
        <div className="grid grid-cols-3 gap-2">
          <StatTile testid="stat-total" icon={Package} label="Total" value={summary.total} accent="var(--ink)" />
          <StatTile testid="stat-low" icon={AlertTriangle} label="Low ≤5" value={summary.low_stock} accent="var(--accent)" />
          <StatTile testid="stat-out" icon={XCircle} label="Out" value={summary.out_of_stock} accent="#8b3a3a" />
        </div>

        {/* Low stock list */}
        <Section title="Needs restocking" subtitle="Running low">
          {summary.low_stock_items.length === 0 ? (
            <Empty text="All stocked well. Good work." />
          ) : (
            <div className="space-y-2">
              {summary.low_stock_items.map((p) => (
                <div
                  key={p.id}
                  data-testid={`low-item-${p.id}`}
                  className="flex items-center gap-3 rounded-2xl p-2.5"
                  style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
                >
                  <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0" style={{ background: "#F1E8D8" }}>
                    {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-[14px] leading-tight truncate">{p.name}</div>
                    <div className="text-[10px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>{p.brand || "—"} · ₹{Math.round(p.selling_price || 0)}</div>
                  </div>
                  <div className="chip-accent text-[10px] tracking-editorial-sm uppercase px-2 py-1 rounded-full">
                    {p.stock_quantity} left
                  </div>
                  <button
                    data-testid={`restock-${p.id}`}
                    onClick={() => nav(`/admin/products/${p.id}`)}
                    className="w-9 h-9 rounded-full grid place-items-center press"
                    style={{ border: "1px solid rgba(27,25,23,0.15)" }}
                    aria-label="Edit"
                  >
                    <ArrowRight size={13} strokeWidth={1.6} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Out of stock list */}
        {summary.out_of_stock_items.length > 0 && (
          <Section title="Out of stock" subtitle="Reorder or hide">
            <div className="space-y-2">
              {summary.out_of_stock_items.map((p) => (
                <div
                  key={p.id}
                  data-testid={`out-item-${p.id}`}
                  className="flex items-center gap-3 rounded-2xl p-2.5 opacity-90"
                  style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
                >
                  <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0" style={{ background: "#F1E8D8" }}>
                    {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-[14px] leading-tight truncate">{p.name}</div>
                    <div className="text-[10px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>{p.brand || "—"}</div>
                  </div>
                  <Link
                    to={`/admin/products/${p.id}`}
                    className="text-[10px] tracking-editorial-sm uppercase underline underline-offset-[6px]"
                    style={{ color: "var(--accent)", textDecorationColor: "var(--accent)" }}
                  >
                    Restock
                  </Link>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Recent sales */}
        <Section title="Recent sales" subtitle={`${sales.length} logged`}>
          {sales.length === 0 ? (
            <Empty text="No sales logged yet. Use the −1 button on a product row to record one." />
          ) : (
            <div className="space-y-2">
              {sales.map((s) => (
                <div
                  key={s.id}
                  data-testid={`sale-${s.id}`}
                  className="flex items-center justify-between rounded-2xl p-3"
                  style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
                >
                  <div className="min-w-0">
                    <div className="font-display text-[14px] leading-tight truncate">{s.product_name}</div>
                    <div className="text-[10px] tracking-editorial-sm uppercase mt-0.5" style={{ color: "var(--ink-3)" }}>
                      {new Date(s.sold_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {" · "}Qty {s.quantity}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display-italic text-[15px]" style={{ color: "var(--accent)" }}>{rupees(s.total)}</div>
                    <div className="text-[9px] tracking-editorial-sm uppercase" style={{ color: "var(--ink-3)" }}>@ ₹{Math.round(s.unit_price)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <div className="h-6" />
      </div>
      <BottomNav />
    </AppFrame>
  );
}

const StatTile = ({ icon: Icon, label, value, accent, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl p-3.5 flex flex-col justify-between aspect-square"
    style={{ background: "var(--paper)", border: "1px solid rgba(27,25,23,0.06)" }}
  >
    <Icon size={18} strokeWidth={1.5} style={{ color: accent }} />
    <div>
      <div className="font-display text-[26px] leading-none" style={{ color: "var(--ink)" }}>{value}</div>
      <div className="text-[9px] tracking-editorial-sm uppercase mt-1" style={{ color: "var(--ink-3)" }}>{label}</div>
    </div>
  </div>
);

const Section = ({ title, subtitle, children }) => (
  <section className="pt-3">
    <div className="mb-2">
      {subtitle && <div className="text-[9px] tracking-editorial uppercase" style={{ color: "var(--ink-3)" }}>{subtitle}</div>}
      <h3 className="font-display text-[18px] leading-tight" style={{ color: "var(--ink)" }}>{title}</h3>
    </div>
    {children}
  </section>
);

const Empty = ({ text }) => (
  <div
    className="rounded-2xl py-6 px-4 text-center"
    style={{ background: "var(--bg-alt)", border: "1px solid rgba(27,25,23,0.06)" }}
  >
    <div className="text-[11px]" style={{ color: "var(--ink-2)" }}>{text}</div>
  </div>
);
